import { useState } from "react"
import { storyblokEditable, renderRichText } from "@storyblok/react"
import { motion } from "framer-motion"
import { Button } from "../../ui/button"

const resolveLink = (link: any): string | null => {
  if (!link) return null
  if (typeof link === "string") return link || null
  if (link.url) return link.url
  if (link.cached_url) {
    const anchor = link.anchor ? `#${link.anchor}` : ""
    const path = link.cached_url.startsWith("/")
      ? link.cached_url
      : `/${link.cached_url}`
    return `${path}${anchor}`
  }
  return null
}

const rtHTML = (rt: any) =>
  (renderRichText(rt || { content: [] }) || "").toString()

const rtPlain = (rt: any) =>
  rtHTML(rt)
    .replace(/<[^>]+>/g, "")
    .replace(/\s+/g, " ")
    .trim()

export default function CoachFeature({ blok }: any) {
  const [isPlaying, setIsPlaying] = useState(false)

  const ctaText = rtPlain(blok?.ctaText)
  const href = resolveLink(blok?.ctaLink)
  const isExternal = !!href && /^(https?:|mailto:|tel:)/i.test(href)

  const raw =
    typeof blok.videoEmbed === "string"
      ? blok.videoEmbed
      : (renderRichText(blok.videoEmbed || { content: [] }) || "").toString()

  const normalized = raw
    .replace(/\s(width|height)="[^"]*"/g, "")
    .replace("<iframe", '<iframe class="w-full h-full"')

  return (
    <section
      {...storyblokEditable(blok)}
      className="relative bg-white py-24 md:py-28 px-6 md:px-12 overflow-visible"
    >
      <div className="max-w-7xl mx-auto grid md:grid-cols-[1fr_1.3fr] lg:grid-cols-[1fr_1.45fr] gap-14 items-start">
        <div className="relative">
          <div
            aria-hidden="true"
            className="absolute -left-4 top-4 bottom-4 w-2 rounded-full bg-gradient-to-b from-[#4750F5] to-[#8BA3FF]"
          />

          <div
            className="font-['BeausiteClassic'] font-bold text-[24px] md:text-[32px] text-gray-900 mb-3"
            dangerouslySetInnerHTML={{
              __html: rtHTML(blok?.heading) || "A Coach’s Perspective",
            }}
          />
          <span
            aria-hidden="true"
            className="block h-[3px] w-16 rounded-full bg-gradient-to-r from-[#4750F5] to-[#8BA3FF] mb-6"
          />

          <div
            className="font-['BeausiteClassic'] text-base md:text-lg text-gray-700 leading-relaxed mb-6"
            dangerouslySetInnerHTML={{
              __html:
                rtHTML(blok?.intro) ||
                "Coaches don’t need more noise—they need systems that actually move the needle. In this short video, Zack shares the real patterns he’s seen across hundreds of coaching businesses—and why getting your website and offers dialed is the unlock for momentum.",
            }}
          />
          <div className="flex items-center gap-4 mb-6">
            {blok?.headshot?.filename ? (
              <img
                src={blok.headshot.filename}
                alt={
                  blok.headshot.alt || rtPlain(blok?.name) || "Coach headshot"
                }
                className="h-14 w-14 rounded-full object-cover ring-2 ring-white shadow"
                loading="lazy"
              />
            ) : null}
            <div>
              <div
                className="font-['BeausiteClassic'] font-bold text-base md:text-lg text-gray-900"
                dangerouslySetInnerHTML={{
                  __html: rtHTML(blok?.name) || "Zack — Coach & Program Lead",
                }}
              />
              <div
                className="font-['BeausiteClassic'] text-sm md:text-base text-gray-600"
                dangerouslySetInnerHTML={{
                  __html:
                    rtHTML(blok?.role) ||
                    "Executive Coach • Community Programs at Zoee",
                }}
              />
            </div>
          </div>

          {blok?.quote?.content?.length ? (
            <div className="relative pl-6 mb-8">
              <span className="absolute left-0 -top-1 text-2xl text-[#CCD3FF] select-none"></span>
              <div
                className="font-['BeausiteClassic'] italic text-gray-800 text-base md:text-lg leading-relaxed"
                dangerouslySetInnerHTML={{ __html: rtHTML(blok?.quote) }}
              />
            </div>
          ) : null}

          {ctaText && href ? (
            <Button asChild variant="default" size="lg">
              <a
                href={href}
                target={isExternal ? "_blank" : "_self"}
                rel={isExternal ? "noopener noreferrer" : undefined}
                aria-label={ctaText}
              >
                {ctaText}
              </a>
            </Button>
          ) : null}
        </div>

        <motion.figure
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
          className="relative"
        >
          <div className="relative aspect-video rounded-2xl overflow-hidden shadow-xl">
            <div
              aria-hidden="true"
              className="absolute -right-8 md:-right-12 -bottom-10 md:-bottom-14 w-[110%] md:w-[118%] h-[52%] md:h-[58%] bg-[#FCB900] rotate-6 rounded-2xl -z-10 shadow-[0_18px_40px_rgba(252,185,0,0.35)]"
            />
            <div
              aria-hidden="true"
              className="absolute -top-6 -left-6 w-40 h-40 rounded-full bg-[#DDE2FF] blur-2xl opacity-80 -z-10"
            />

            {!isPlaying ? (
              <button
                className="group absolute inset-0 w-full h-full flex items-center justify-center focus:outline-none"
                onClick={() => setIsPlaying(true)}
                aria-label="Play video"
              >
                {blok?.coverImage?.filename ? (
                  <img
                    src={blok.coverImage.filename}
                    alt={blok.coverImage?.alt || "Video cover"}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full bg-[#EEF1FF]" />
                )}

                <div className="absolute flex items-center justify-center w-20 h-20 bg-white/90 rounded-full shadow-lg group-hover:scale-105 transition-transform">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="#4750F5"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="#4750F5"
                    className="w-10 h-10"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5.25 5.653v12.694c0 .865.93 1.398 1.693.96l10.963-6.347a1.125 1.125 0 000-1.92L6.943 4.693a1.125 1.125 0 00-1.693.96z"
                    />
                  </svg>
                </div>
              </button>
            ) : (
              <div
                className="absolute inset-0"
                dangerouslySetInnerHTML={{ __html: normalized }}
              />
            )}
          </div>
          {blok?.videoCaption?.content?.length ? (
            <figcaption
              className="mt-3 text-sm text-gray-500"
              dangerouslySetInnerHTML={{ __html: rtHTML(blok?.videoCaption) }}
            />
          ) : null}
        </motion.figure>
      </div>
    </section>
  )
}
